
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { checkPaymentStatus } from "@/api/check-payment-status";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, RefreshCw, RotateCw, AlertCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

type PaymentTransaction = {
  id: string;
  user_id: string;
  course_id: string;
  amount: number;
  payment_id: string;
  transaction_ref?: string;
  status: string;
  created_at: string;
  payment_method: string | null;
  user_email?: string;
  course_title?: string;
};

const PaymentTransactionsAdmin = () => {
  const [transactions, setTransactions] = useState<PaymentTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState<{[key: string]: boolean}>({});
  const [errorDialog, setErrorDialog] = useState<{
    open: boolean, 
    message: string, 
    title: string,
    status?: string
  }>({
    open: false,
    message: '',
    title: '',
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      setIsLoading(true);
      
      const { data: attemptData, error: attemptError } = await supabase
        .from("course_payment_attempts")
        .select(`
          id, user_id, course_id, amount, payment_id, transaction_ref, status, created_at
        `)
        .order("created_at", { ascending: false });
      
      if (attemptError) throw attemptError;
      
      const userIds = [...new Set(attemptData.map((item: any) => item.user_id))];
      const { data: userData, error: userError } = await supabase
        .from("profiles")
        .select("id, email")
        .in("id", userIds);
      
      if (userError) throw userError;
      
      const userEmailMap = new Map();
      userData?.forEach((user: any) => {
        userEmailMap.set(user.id, user.email);
      });
      
      const courseIds = [...new Set(attemptData.map((item: any) => item.course_id))];
      const { data: courseData, error: courseError } = await supabase
        .from("courses")
        .select("id, title")
        .in("id", courseIds);
      
      if (courseError) throw courseError;
      
      const courseTitleMap = new Map();
      courseData?.forEach((course: any) => {
        courseTitleMap.set(course.id, course.title);
      });
      
      const { data: completedPayments, error: paymentsError } = await supabase
        .from("course_payments")
        .select("payment_id, payment_method, status");
      
      if (paymentsError) throw paymentsError;
      
      const paymentDetailsMap = new Map();
      completedPayments?.forEach((payment: any) => {
        paymentDetailsMap.set(payment.payment_id, {
          status: payment.status,
          payment_method: payment.payment_method
        });
      });
      
      const formattedTransactions = attemptData.map((item: any) => {
        const paymentDetails = paymentDetailsMap.get(item.payment_id);
        
        return {
          id: item.id,
          user_id: item.user_id,
          course_id: item.course_id,
          amount: item.amount,
          payment_id: item.payment_id,
          transaction_ref: item.transaction_ref,
          status: paymentDetails?.status || item.status,
          created_at: item.created_at,
          payment_method: paymentDetails?.payment_method || null,
          user_email: userEmailMap.get(item.user_id),
          course_title: courseTitleMap.get(item.course_id)
        };
      });
      
      setTransactions(formattedTransactions);
    } catch (error: any) {
      console.error("Error fetching transactions:", error);
      toast({
        title: "Failed to load transactions",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = async () => {
    if (isRefreshing) return;
    
    try {
      setIsRefreshing(true);
      
      const pendingTransactions = transactions.filter(t => t.status === "pending" && t.payment_id);
      
      if (pendingTransactions.length === 0) {
        toast({
          title: "No pending transactions",
          description: "There are no pending transactions to refresh.",
        });
        return;
      }
      
      const refreshPromises = pendingTransactions.map(async (transaction) => {
        if (!transaction.payment_id) return;
        
        try {
          const response = await fetch("/api/check-payment-status", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              paymentId: transaction.payment_id,
              courseId: transaction.course_id,
              userId: transaction.user_id,
              transactionRef: transaction.transaction_ref
            })
          });
          
          if (!response.ok) {
            throw new Error(`Failed to check payment status: ${response.statusText}`);
          }
          
          return await response.json();
        } catch (error) {
          console.error(`Error checking payment ${transaction.payment_id}:`, error);
          return null;
        }
      });
      
      await Promise.all(refreshPromises);
      
      toast({
        title: "Transactions refreshed",
        description: "Payment statuses have been updated.",
      });
      
      await fetchTransactions();
      
    } catch (error: any) {
      console.error("Error refreshing transactions:", error);
      toast({
        title: "Refresh failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsRefreshing(false);
    }
  };
  
  const handleCheckSingleStatus = async (transaction: PaymentTransaction) => {
    if (!transaction.payment_id && !transaction.transaction_ref) {
      toast({
        title: "Missing payment information",
        description: "This transaction has no payment ID or reference number.",
        variant: "destructive",
      });
      return;
    }
    
    if (checkingStatus[transaction.id]) return;
    
    try {
      setCheckingStatus(prev => ({ ...prev, [transaction.id]: true }));
      
      toast({
        title: "Checking payment status",
        description: `Verifying payment ${transaction.payment_id ? transaction.payment_id.substring(0, 8) : transaction.transaction_ref}...`,
      });
      
      const requestPayload = {
        paymentId: transaction.payment_id || "",
        courseId: transaction.course_id,
        userId: transaction.user_id,
        transactionRef: transaction.transaction_ref || ""
      };
      
      console.log("Checking payment with payload:", requestPayload);
      
      try {
        const responseData = await checkPaymentStatus(requestPayload);
        
        console.log("Status check response:", responseData);
        
        if (responseData.success) {
          if (responseData.status === 'completed') {
            toast({
              title: "Payment verified",
              description: "Payment status has been updated to completed",
            });
          } else {
            toast({
              title: "Payment status",
              description: `Payment is ${responseData.status}. ${responseData.message || ''}`,
            });
          }
          
          await fetchTransactions();
        } else {
          // Show error dialog with more details
          setErrorDialog({
            open: true,
            title: "Status check failed",
            message: responseData.message || "Could not verify payment status",
            status: responseData.status || "error"
          });
        }
      } catch (error: any) {
        console.error("Error checking payment status:", error);
        setErrorDialog({
          open: true,
          title: "Error checking status",
          message: error.message,
          status: "error"
        });
      }
    } catch (error: any) {
      console.error("Error checking payment status:", error);
      setErrorDialog({
        open: true,
        title: "Error checking status",
        message: error.message,
        status: "error"
      });
    } finally {
      setCheckingStatus(prev => ({ ...prev, [transaction.id]: false }));
    }
  };
  
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };
  
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "EGP"
    }).format(amount);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Payment Transactions</h2>
        <Button 
          onClick={handleRefresh} 
          disabled={isRefreshing || isLoading}
          className="flex items-center gap-2"
        >
          {isRefreshing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          Refresh Pending Payments
        </Button>
      </div>
      
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <Card>
          <CardHeader className="pb-3">
            <h3 className="text-lg font-medium">All Transactions</h3>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Course</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Order ID</TableHead>
                  <TableHead>Internal Ref</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No transactions found
                    </TableCell>
                  </TableRow>
                ) : (
                  transactions.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell>{formatDate(transaction.created_at)}</TableCell>
                      <TableCell>{transaction.course_title || "Unknown Course"}</TableCell>
                      <TableCell>{transaction.user_email || "Unknown User"}</TableCell>
                      <TableCell>{formatCurrency(transaction.amount)}</TableCell>
                      <TableCell>
                        <span className="text-xs font-mono">
                          {transaction.payment_id || "N/A"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs font-mono">
                          {transaction.transaction_ref || "N/A"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant={
                            transaction.status === "completed" 
                              ? "default" 
                              : transaction.status === "pending" 
                                ? "secondary" 
                                : "destructive"
                          }
                        >
                          {transaction.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {(transaction.payment_id || transaction.transaction_ref) && transaction.status !== "completed" && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleCheckSingleStatus(transaction)}
                            disabled={checkingStatus[transaction.id]}
                            className="h-8 px-2 text-xs flex items-center gap-1"
                          >
                            {checkingStatus[transaction.id] ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <RotateCw className="h-3 w-3" />
                            )}
                            Check Status
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
      
      <Dialog open={errorDialog.open} onOpenChange={(open) => setErrorDialog(prev => ({...prev, open}))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              {errorDialog.title}
            </DialogTitle>
            <DialogDescription className="pt-2">
              <div className="text-center mb-2">
                {errorDialog.status === "pending" ? (
                  <Badge variant="secondary">Payment is pending</Badge>
                ) : (
                  <Badge variant="destructive">Status check failed</Badge>
                )}
              </div>
              <p>{errorDialog.message}</p>
              {errorDialog.status === "pending" && (
                <p className="mt-2 text-muted-foreground text-sm">
                  The payment status is still pending. This could be because:
                  <ul className="list-disc ml-5 mt-1">
                    <li>The payment is still being processed</li>
                    <li>The webhook hasn't received the payment confirmation yet</li>
                    <li>There was a delay in the payment provider's system</li>
                  </ul>
                </p>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-center">
            <Button variant="outline" onClick={() => setErrorDialog(prev => ({...prev, open: false}))}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentTransactionsAdmin;
